import { fareRows } from "@/components/FareTable";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";

const stamp = (r) => r.updated_at || r.created_at || "";
const points = (r) => [{ id: r.origin_id, km: 0, min: 0 }, ...(r.stops || []).map((s) => ({ id: s.station_id, km: Number(s.distance_km || 0), min: Number(s.minutes || 0) })),
  { id: r.destination_id, km: Number(r.distance_km || 0), min: Number(r.duration_min || 0) }];

// Latest-updated timetable that travels a -> b, with the km / minutes / fare between them
function lookup(hist, a, b) {
  for (const r of hist) {
    const p = points(r);
    const i = p.findIndex((x) => x.id === a);
    const j = p.findIndex((x, k) => k > i && x.id === b);
    if (i >= 0 && j > i) return { r, km: p[j].km - p[i].km, min: p[j].min - p[i].min, fare: (r.fares || []).find((f) => f.from_id === a && f.to_id === b) };
  }
  return null;
}

const blank = (v) => v === "" || v === null || v === undefined;

// Road KM / minutes from station GPS (OSRM). Returns a merger for the latest form, or null.
export async function gpsMetrics(form, key, prev) {
  if (!["origin_id", "destination_id", "stops"].includes(key)) return null;
  const idsOf = (f) => [f?.origin_id, ...((f?.stops) || []).map((s) => s.station_id), f?.destination_id];
  const ids = idsOf(form);
  if (prev && idsOf(prev).join(",") === ids.join(",")) return null;
  if (ids.some((x) => !x) || ids.length < 2) return null;
  const sig = ids.join(",");
  try {
    const { data } = await api.get("/admin/geo/route-metrics", { params: { ids: sig } });
    const by = Object.fromEntries(data.slice(1, -1).map((p) => [p.station_id, p]));
    const end = data[data.length - 1];
    return (f) => {
      const cur = [f.origin_id, ...(f.stops || []).map((s) => s.station_id), f.destination_id].join(",");
      if (cur !== sig) return f;
      return { ...f, distance_km: end.km, duration_min: end.minutes, stops: (f.stops || []).map((s) => (by[s.station_id] ? { ...s, distance_km: by[s.station_id].km, minutes: by[s.station_id].minutes } : s)) };
    };
  } catch (e) {
    toast.message(errMsg(e));
    return null;
  }
}

// Auto-fills empty KM, minutes, departure times and fares from the latest saved timetables
export function fillFromHistory(next, key, routes) {
  if (!["origin_id", "destination_id", "stops"].includes(key)) return next;
  const hist = routes.filter((r) => r.id !== next.id && r.fares).sort((x, y) => stamp(y).localeCompare(stamp(x)));
  const o = next.origin_id, d = next.destination_id;
  const out = { ...next };
  const full = o && d ? lookup(hist, o, d) : null;
  if (full) {
    if (!Number(out.distance_km)) out.distance_km = full.km;
    if (!Number(out.duration_min)) out.duration_min = full.min;
    const same = full.r.origin_id === o && full.r.destination_id === d;
    if (same && !out.id && key !== "stops") {
      out.times = full.r.times || out.times;
      out.weekdays = full.r.weekdays || out.weekdays;
      if (!out.bus_type_id) out.bus_type_id = full.r.bus_type_id;
    }
  }
  out.stops = (out.stops || []).map((s) => {
    const m = o && s.station_id ? lookup(hist, o, s.station_id) : null;
    return m ? { ...s, minutes: blank(s.minutes) ? m.min : s.minutes, distance_km: blank(s.distance_km) ? m.km : s.distance_km } : s;
  });
  out.fares = fareRows(out).map((row) => {
    if (!blank(row.fare)) return row;
    const m = lookup(hist, row.from_id, row.to_id);
    return m?.fare ? { ...row, fare: m.fare.fare, online: m.fare.online } : row;
  });
  return out;
}
